import { isMobileDevice, isLowMemoryDevice, isSlowNetworkConnection } from './deviceDetection';

interface PerformanceMark {
  name: string;
  timestamp: number;
  duration?: number;
  isMobile?: boolean;
  isLowMemory?: boolean;
  isSlowNetwork?: boolean;
}

interface MemoryInfo {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
  jsHeapSizeLimit?: number;
}

class PerformanceMonitor {
  private marks: Map<string, number> = new Map();
  private measurements: PerformanceMark[] = [];
  private maxMeasurements = 100;
  private memorySnapshots: MemoryInfo[] = [];
  private maxMemorySnapshots = 50;

  mark(name: string) {
    const timestamp = Date.now();
    this.marks.set(name, timestamp);
    console.log(`[PerformanceMonitor] Mark: ${name} at ${timestamp}`);
  }

  measure(name: string, startMark: string, endMark?: string) {
    const startTime = this.marks.get(startMark);
    const endTime = endMark ? this.marks.get(endMark) : Date.now();

    if (!startTime) {
      console.warn(`[PerformanceMonitor] Start mark not found: ${startMark}`);
      return;
    }

    const duration = endTime - startTime;
    const measurement: PerformanceMark = {
      name,
      timestamp: startTime,
      duration,
      isMobile: isMobileDevice(),
      isLowMemory: isLowMemoryDevice(),
      isSlowNetwork: isSlowNetworkConnection(),
    };

    this.measurements.push(measurement);

    if (this.measurements.length > this.maxMeasurements) {
      this.measurements.shift();
    }

    console.log(`[PerformanceMonitor] ${name}: ${duration}ms`);

    const threshold = measurement.isMobile ? 8000 : 5000;
    if (duration > threshold) {
      console.warn(`[PerformanceMonitor] SLOW OPERATION: ${name} took ${duration}ms (mobile: ${measurement.isMobile})`);
    }

    return duration;
  }

  takeMemorySnapshot() {
    if (typeof window === 'undefined') return;

    const performance = (window.performance as any);
    if (performance?.memory) {
      const snapshot: MemoryInfo = {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
      };

      this.memorySnapshots.push(snapshot);

      if (this.memorySnapshots.length > this.maxMemorySnapshots) {
        this.memorySnapshots.shift();
      }

      const usedMB = (snapshot.usedJSHeapSize || 0) / (1024 * 1024);
      const limitMB = (snapshot.jsHeapSizeLimit || 0) / (1024 * 1024);
      const percentUsed = (usedMB / limitMB) * 100;

      if (percentUsed > 80) {
        console.warn(`[PerformanceMonitor] HIGH MEMORY USAGE: ${usedMB.toFixed(2)}MB / ${limitMB.toFixed(2)}MB (${percentUsed.toFixed(1)}%)`);
      }

      return snapshot;
    }

    return null;
  }

  getMemoryStats() {
    if (this.memorySnapshots.length === 0) {
      return null;
    }

    const latest = this.memorySnapshots[this.memorySnapshots.length - 1];
    const usedMB = (latest.usedJSHeapSize || 0) / (1024 * 1024);
    const totalMB = (latest.totalJSHeapSize || 0) / (1024 * 1024);
    const limitMB = (latest.jsHeapSizeLimit || 0) / (1024 * 1024);

    return {
      usedMB: usedMB.toFixed(2),
      totalMB: totalMB.toFixed(2),
      limitMB: limitMB.toFixed(2),
      percentUsed: ((usedMB / limitMB) * 100).toFixed(1),
    };
  }

  getMeasurements(filterPattern?: string): PerformanceMark[] {
    if (!filterPattern) {
      return this.measurements;
    }

    return this.measurements.filter(m => m.name.includes(filterPattern));
  }

  getSlowestMeasurements(limit: number = 10): PerformanceMark[] {
    return [...this.measurements]
      .filter(m => m.duration !== undefined)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0))
      .slice(0, limit);
  }

  clear() {
    this.marks.clear();
    this.measurements = [];
    console.log('[PerformanceMonitor] Cleared all measurements');
  }

  getStats() {
    const durations = this.measurements
      .map(m => m.duration)
      .filter((d): d is number => d !== undefined);

    if (durations.length === 0) {
      return {
        count: 0,
        average: 0,
        min: 0,
        max: 0,
      };
    }

    return {
      count: durations.length,
      average: durations.reduce((a, b) => a + b, 0) / durations.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
    };
  }

  logReport() {
    console.group('[PerformanceMonitor] Performance Report');
    console.log('Device:', {
      isMobile: isMobileDevice(),
      isLowMemory: isLowMemoryDevice(),
      isSlowNetwork: isSlowNetworkConnection(),
    });
    console.log('Total measurements:', this.measurements.length);
    console.log('Stats:', this.getStats());
    console.log('Memory:', this.getMemoryStats());
    console.log('Slowest operations:', this.getSlowestMeasurements(5));
    console.groupEnd();
  }

  startMemoryMonitoring(intervalMs: number = 30000) {
    const intervalId = setInterval(() => {
      this.takeMemorySnapshot();
    }, intervalMs);

    return () => clearInterval(intervalId);
  }
}

export const performanceMonitor = new PerformanceMonitor();

if (typeof window !== 'undefined') {
  (window as any).__PERFORMANCE_MONITOR__ = performanceMonitor;
  (window as any).__PERF_REPORT__ = () => performanceMonitor.logReport();

  console.log('[PerformanceMonitor] Available via window.__PERFORMANCE_MONITOR__ and window.__PERF_REPORT__()');
}
